import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import {
  SpaceDocument,
  SpacesDocument,
  useUpdateSpaceMutation,
} from "#graphql/supergraphHooks";
import {
  type UpdateSpaceInput,
  type UpdateSpaceMutation,
  type UpdateSpaceMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseUpdateSpaceReturn {
  updateSpace: (input: UpdateSpaceInput) => Promise<UpdateSpaceMutation["updateSpace"] | null>;
  loading: boolean;
  error: ReturnType<typeof useUpdateSpaceMutation>[1]["error"];
}

/**
 * DV-218 — full-state update of a Space. Fields omitted from `input` are left
 * unchanged; `clients` / `attributeAssignments`, when supplied, replace current
 * state (attributes are never deleted; domain is immutable). Returns the updated
 * Space, or `null` if the mutation returned no data.
 *
 * @example
 * const { updateSpace, loading } = useUpdateSpace();
 * const space = await updateSpace({ spaceId, name: "Renamed" });
 */
export const useUpdateSpace = (
  options?: MutationHookOptions<UpdateSpaceMutation, UpdateSpaceMutationVariables>,
): UseUpdateSpaceReturn => {
  const [mutate, { loading, error }] = useUpdateSpaceMutation({
    // Refresh any mounted Spaces list and the Space detail so they reflect the
    // update. Query hooks read `no-cache`, so refetching the active queries is
    // the mechanism (a cache write wouldn't surface). Overridable via `options`.
    refetchQueries: [SpacesDocument, SpaceDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const updateSpace = useCallback(
    async (input: UpdateSpaceInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.updateSpace ?? null;
    },
    [mutate],
  );

  return { updateSpace, loading, error };
};
